import { Button, FullWidthBanner } from '@systemeio/components';
import { XCloseIcon } from '@systemeio/icons';
import { CurrencyEnum } from '@systemeio/types';
import { centsToUnits, useSessionStore } from '@systemeio/util';
import { useTotalRevenueLimit } from 'modules/main/hooks/use-total-revenue-limit';
import { useMemo, useState } from 'react';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import TransLoco from 'shared/components/trans-loco';
import { pageRoute } from 'shared/constants/routes.constants';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { CONTENT_CLASSNAME } from 'shared/layout/constants/layout-class-names';
import { currencyFormat } from 'shared/utils/currency-format';
import { formatExactDate } from 'shared/utils/date-time-format';
import { getPageRoute } from 'shared/utils/get-dynamic-page-route';
const MIN_REVENUE_LIMIT_PERCENTAGE = 80;
const REVENUE_LIMIT_NOTIFICATION_CLOSED_KEY = 'revenueLimitNotificationClosed';
enum RevenueLimitStageEnum {
  warning = 'warning',
  gracePeriod = 'gracePeriod',
  restricted = 'restricted',
}
const getRevenueLimitAccentClassName = (percentage: number) => {
  if (percentage >= 95) {
    return 'text-text-error';
  }
  if (percentage >= 90) {
    return 'text-text-error';
  }
  return 'text-text-warning';
};
export const RevenueLimitNotification = () => {
  const {
    t
  } = useLocoTranslation();
  const {
    user,
    intlLocale
  } = useUser();
  const {
    totalRevenueLimit
  } = useTotalRevenueLimit();
  const notificationState = useSessionStore<boolean>(REVENUE_LIMIT_NOTIFICATION_CLOSED_KEY, {
    useSessionStorage: true
  });
  const [isClosed, setIsClosed] = useState(() => notificationState.getItem() === true);
  const notificationData = useMemo(() => {
    if (!totalRevenueLimit || !user) return null;
    const revenueLimitInCents = totalRevenueLimit.maxLimit;
    const totalRevenueInCents = totalRevenueLimit.current;
    if (!revenueLimitInCents || totalRevenueInCents === null || totalRevenueInCents === undefined) {
      return null;
    }
    const limit = currencyFormat(centsToUnits(revenueLimitInCents), CurrencyEnum.US_DOLLAR, user.dashboardLocale, true);
    const percentage = Math.floor(totalRevenueInCents / revenueLimitInCents * 100);
    if (user.planLimitExceededAt && percentage >= MIN_REVENUE_LIMIT_PERCENTAGE) {
      const restrictionDate = user.restrictionEstimateDate ? new Date(user.restrictionEstimateDate) : null;
      if (restrictionDate && restrictionDate.getTime() > Date.now()) {
        return {
          date: formatExactDate(restrictionDate, {
            intlLocale,
            timeZone: user.timezone
          }),
          limit,
          percentage,
          stage: RevenueLimitStageEnum.gracePeriod as const
        };
      }
      return {
        limit,
        percentage,
        stage: RevenueLimitStageEnum.restricted as const
      };
    }
    if (percentage < MIN_REVENUE_LIMIT_PERCENTAGE) {
      return null;
    }
    return {
      limit,
      percentage,
      stage: RevenueLimitStageEnum.warning as const
    };
  }, [totalRevenueLimit, user]);
  const handleClose = () => {
    notificationState.setItem(true);
    setIsClosed(true);
  };
  const {
    limit: limit_0,
    stage
  } = notificationData ?? {};
  const isDismissible = stage === RevenueLimitStageEnum.warning;
  if (!notificationData || isClosed && isDismissible) {
    return null;
  }
  const accentClassName = getRevenueLimitAccentClassName(notificationData.percentage);
  return <FullWidthBanner className="-mt-5 w-screen self-center" contentClassName={CONTENT_CLASSNAME} variant={notificationData.percentage >= 90 ? 'error' : 'warning'} actions={isDismissible ? <Button aria-label="Close revenue limit notification" size={'icon-sm'} variant={'ghost-gray'} onClick={handleClose}>
            <XCloseIcon />
          </Button> : null}>
      {stage === RevenueLimitStageEnum.warning && <TransLoco t={t} i18nKey="dashboard.main.revenue_limit_notification" values={{
      maxLimit: limit_0
    }} components={{
      a: <LinkWithoutPrefetch className={'link'} href={getPageRoute(pageRoute.ProfileUserPlan)} />,
      icon: <></>,
      percentage: <span className={accentClassName}>{notificationData.percentage}%</span>
    }} />}
      {stage === RevenueLimitStageEnum.gracePeriod && <TransLoco t={t} i18nKey="dashboard.main.revenue_limit_notification_grace_period" values={{
      date: notificationData.date,
      maxLimit: limit_0
    }} components={{
      a: <LinkWithoutPrefetch className={'link'} href={getPageRoute(pageRoute.ProfileUserPlan)} />,
      icon: <></>,
      span: <span className={accentClassName} />
    }} />}
      {stage === RevenueLimitStageEnum.restricted && <TransLoco t={t} i18nKey="dashboard.main.revenue_limit_notification_reached" values={{
      maxLimit: limit_0
    }} components={{
      a: <LinkWithoutPrefetch className={'link'} href={getPageRoute(pageRoute.ProfileUserPlan)} />,
      icon: <></>
    }} />}
    </FullWidthBanner>;
};